import { getZone, fetchCarrierRoutes } from "components/audience-map/utils";
import type { RecommendedZone } from "./TargetingContext";
import type { TargetingCity } from "./cities";
import { deriveUnits } from "./d2dUnits";

// A selectable D2D distribution polygon (a mission boundary / carrier route).
export interface D2dBoundary {
  id: string;
  /** Polygon rings in [{lat,lng}…] form, as the legacy data-layer renderer expects. */
  paths: unknown;
  name: string;
  /**
   * Polygon centroid — used to recentre the map when focusing the area. Optional
   * because a boundary may arrive without one; the pan-to-area focus simply
   * no-ops then (it guards on numeric coords) rather than jumping to (0,0).
   */
  center?: { lat: number; lng: number };
  /** Auto-derived coverage units (delivery points), never user-entered (T8). */
  units: number;
  /** Raw GeoJSON geometry — non-US areas send it to findNumberHouseholds (see d2dUnits). */
  geometry?: unknown;
}

// A `loadData` from useOverlaysFetch, narrowed to what the boundary fetch needs.
type LoadData = (args: {
  layer: string;
  area?: unknown;
  exclusionZones?: unknown;
  includeAddressCount?: boolean;
  after?: string | null;
}) => Promise<{ data?: Record<string, any>; pageInfo?: { hasNextPage?: boolean; endCursor?: string } } | undefined>;

// Units read off the feature are only real for US carrier routes; non-US areas are
// backfilled from findNumberHouseholds once selected (PAD-33564, see d2dUnits).
const toBoundary = (overlay: any): D2dBoundary => ({
  id: String(overlay.id),
  paths: overlay.paths,
  name: overlay.name ?? "",
  center: overlay.center,
  units: deriveUnits(overlay.metadata),
  geometry: overlay.geometry,
});

// Fetches the city's selectable D2D polygons. US uses paginated carrier routes
// (no zone filter — the whole city), every other country uses MissionBoundaries
// clipped to the city's distribution zone(s) minus its exclusion zone(s). Both
// paths return the same boundary shape with auto-derived units.
export const fetchD2dBoundaries = async ({
  city,
  zones,
  countryCode,
  loadData,
}: {
  city: TargetingCity;
  zones: RecommendedZone[];
  countryCode: string;
  loadData: LoadData;
}): Promise<D2dBoundary[]> => {
  if (countryCode === "US") {
    const routes = await fetchCarrierRoutes({
      cityId: String(city.id),
      cityName: city.name,
      updateMap: () => undefined,
    });
    return Object.values(routes ?? {}).map(toBoundary);
  }

  // getZone unions the distribution / exclusion zone geometries (turf) the same
  // way the legacy D2D map builds its fetch area.
  const distribution = getZone({ distributionZones: zones }, "distribution");
  const exclusion = getZone({ distributionZones: zones }, "exclusion");
  if (!distribution?.geometry) return [];

  const collected: Record<string, any> = {};
  let after: string | null | undefined;
  // Bounded loop so a misbehaving cursor can't spin forever.
  for (let page = 0; page < 50; page += 1) {
    const res = await loadData({
      layer: "MissionBoundaries",
      area: distribution.geometry,
      exclusionZones: exclusion?.geometry ?? null,
      includeAddressCount: true,
      after,
    });
    Object.assign(collected, res?.data ?? {});
    if (!res?.pageInfo?.hasNextPage) break;
    after = res.pageInfo.endCursor;
  }
  return Object.values(collected).map(toBoundary);
};
